'use strict';
import {key} from 'firebase-key';
import get from 'lodash/get';
import memoize from 'lodash/memoize';
import {STATE_CHANGE_SUCCESS} from 'redux-ui-router/lib/action-types';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import {makeActionCreator as libMakeActionCreator} from '../../_libs/redux-observable-list';
import {getRouter} from '../../config/reducers/state';


export function buildListSupportEpicsNavHelper(actions) {
  return ({prevParentId, nextParentId}) => {
    if (prevParentId) {
      return Observable.of(actions.destroyList(void 0, {parentId: prevParentId}));
    } else {
      return Observable.empty();
    }
  };
}

/**
 * @param {String} prefix
 * @return {function(): string}
 */
export function buildQueueIdGenerator(prefix) {
  return () => `${prefix}-${key()}`;
}

/**
 * The resolver function needs to create a hash from all properties being used to get the current style.
 * We are memoizing this function to limit performance impact on the render function.
 */
export const getItemClass = memoize(model => {
  const classes = [];
  if (model.isVisible === false)
    classes.push('hide-elem');
  if (model.isDeleting === true)
    classes.push('removing-elem');

  return classes.length > 0 ? classes.join(' ') : void 0;
}, model => (model.isVisible === false ? 1 : 0) + (model.isDeleting === true ? 4 : 2));

/**
 * @param {String} prefix
 * @param {String} type
 * @return {Function|ReduxActionCreator}
 */
export function makeActionCreator(prefix, type) {
  return libMakeActionCreator(`${prefix}/${type}`);
}

/**
 * @param {String} prefix
 * @param {Object} data
 * @param {String} data.type
 * @param {Function} data.payload Post-process function to modify the payload in the action creator
 * @return {Function|ReduxActionCreator}
 */
export function makePayloadActionCreator(prefix, data) {
  const type = `${prefix}/${data.type}`;
  const customizer = data.payload;
  const action = function (payload={}, meta) {
    return {
      type,
      payload: customizer(payload),
      meta,
    };
  };

  action.TYPE = type;

  return action;
}

/**
 * @param {String} prefix
 * @param {Array<{name: String, type: String, payload: Function}>} actionData
 * @param {Object<String, Function|ReduxActionCreator>} [actions={}]
 * @return {Object<String, Function|ReduxActionCreator>}
 */
export function makeActionCreators(prefix, actionData, actions = {}) {
  return actionData.reduce((acc, data) => {
    acc[data.name] = data.payload ? makePayloadActionCreator(prefix, data) : makeActionCreator(prefix, data.type);
    return acc;
  }, actions);
}

/**
 * Emit action when new currentState is found in names
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Array<String>} names
 * @param names
 * @return {Observable<ReduxAction>}
 */
export function onEnterView(action$, names) {
  return action$.ofType(STATE_CHANGE_SUCCESS)
    .map(action => {
      if (isInView(action.payload, names)) {
        return action;
      } else {
        return null;
      }
    })
    .filter(action => !!action);
}

/**
 * @param {Object} routerState
 * @param {Array<String>} stateNames
 * @return {Boolean}
 */
export function isInView(routerState, stateNames) {
  return stateNames.includes(get(routerState, 'currentState.name'));
}

/**
 * @param {Array<String>} stateNames
 * @return {function(Object): Boolean}
 */
export function isInViewCurried(stateNames) {
  /**
   * @param {Object} routerState
   * @return {Boolean}
   */
  return routerState => isInView(routerState, stateNames);
}

/**
 * @param {Observable<any>} state$
 * @param {Array<String>} stateNames
 * @return {Observable<any>}
 */
export function getStateParamsInView$(state$, stateNames) {
  return state$
    .map(getRouter)
    .filter(isInViewCurried(stateNames))
    .map(routerState => routerState.currentParams)
    .distinctUntilChanged();
}

/**
 * Emit action when new currentState is found in names
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Array<String>} names
 * @param names
 * @return {Observable<ReduxAction>}
 */
export function onLeaveView(action$, names) {
  return action$.ofType(STATE_CHANGE_SUCCESS)
    .map(action => {
      if (names.includes(get(action.payload, 'prevState.name'))) {
        return action;
      } else {
        return null;
      }
    })
    .filter(action => !!action);
}

export function getPropsFromState(state$, getState, properties) {
  return state$
    .map(getState)
    .distinctUntilChanged(compareProperties(properties))
    .map(state => {
      const props = {};

      properties.forEach(prop => props[prop] = state[prop]);

      return props;
    });
}

function compareProperties(properties) {
  const len = properties.length;
  return (a, b) => {
    if (a === b || (!a && !b)) {
      return true;
    } else {
      let p;
      for (let i = 0; i < len; i++) {
        p = properties[i];
        if (a[p] !== b[p]) {
          return false;
        }
      }
      return true;
    }
  };
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux/utils.js